import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import './MonacoSearchPanel.css';

const MonacoSearchPanel = ({projectId, serviceName, onFileSelect, onClose}) => {
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [includePattern, setIncludePattern] = useState('');
  const [excludePattern, setExcludePattern] = useState('');
  const [expandedFiles, setExpandedFiles] = useState({});

  // Debounced search
  useEffect(() => {
    if (!searchText.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, caseSensitive, wholeWord, useRegex, includePattern, excludePattern, projectId, serviceName]);

  const performSearch = useCallback(async () => {
    if (!searchText.trim() || !projectId || !serviceName) {
      console.log('🔍 Search aborted - missing params:', {searchText: searchText.trim(), projectId, serviceName});
      return;
    }

    console.log('🔍 Performing search:', {searchText, projectId, serviceName});
    setIsSearching(true);

    try {
      // Import the storage module
      const {default: indexedDbFileStorage} = await import('../utils/indexedDbFileStorage');
      
      // Get all files from IndexedDB
      const allFiles = await indexedDbFileStorage.getAllFiles(projectId, serviceName);
      console.log('📂 Files from IndexedDB:', allFiles?.length || 0);
      
      if (!allFiles || allFiles.length === 0) {
        console.log('⚠️ No files found in IndexedDB for search');
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      // Create search regex
      let searchPattern;
      try {
        if (useRegex) {
          searchPattern = new RegExp(searchText, caseSensitive ? 'g' : 'gi');
        } else {
          const escapedText = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const pattern = wholeWord ? `\\b${escapedText}\\b` : escapedText;
          searchPattern = new RegExp(pattern, caseSensitive ? 'g' : 'gi');
        }
      } catch (e) {
        // Invalid regex
        setIsSearching(false);
        return;
      }

      // Filter by include/exclude patterns
      const includeRegex = includePattern ? new RegExp(includePattern, 'i') : null;
      const excludeRegex = excludePattern ? new RegExp(excludePattern, 'i') : null;

      const results = [];

      for (const file of allFiles) {
        // Apply file path filters
        if (includeRegex && !includeRegex.test(file.filePath)) continue;
        if (excludeRegex && excludeRegex.test(file.filePath)) continue;

        const content = file.content || '';
        const lines = content.split('\n');
        const fileMatches = [];

        lines.forEach((line, lineIndex) => {
          // Reset regex lastIndex for each line
          searchPattern.lastIndex = 0;
          
          const matches = [];
          let match;
          
          while ((match = searchPattern.exec(line)) !== null) {
            matches.push({
              start: match.index,
              end: match.index + match[0].length,
              text: match[0]
            });
            
            // Prevent infinite loop for zero-length matches
            if (match.index === searchPattern.lastIndex) {
              searchPattern.lastIndex++;
            }
          }

          if (matches.length > 0) {
            fileMatches.push({
              lineNumber: lineIndex + 1,
              lineText: line,
              matches
            });
          }
        });

        if (fileMatches.length > 0) {
          results.push({
            filePath: file.filePath,
            fileName: file.fileName || file.filePath.split('/').pop(),
            matches: fileMatches,
            totalMatches: fileMatches.reduce((sum, m) => sum + m.matches.length, 0)
          });
        }
      }

      console.log('✅ Search completed:', results.length, 'files with matches');
      setSearchResults(results);
      setIsSearching(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Search error:', error);
      setIsSearching(false);
    }
  }, [searchText, caseSensitive, wholeWord, useRegex, includePattern, excludePattern, projectId, serviceName]);

  const toggleFileExpanded = (filePath) => {
    setExpandedFiles(prev => ({
      ...prev,
      [filePath]: !prev[filePath]
    }));
  };

  const handleMatchClick = (filePath, lineNumber) => {
    if (onFileSelect) {
      onFileSelect(filePath, lineNumber);
    }
  };

  const highlightMatches = (lineText, matches) => {
    if (!matches || matches.length === 0) {
      return <span>{lineText}</span>;
    }

    const parts = [];
    let lastIndex = 0;

    // Sort matches by start position
    const sortedMatches = [...matches].sort((a, b) => a.start - b.start);

    sortedMatches.forEach((match, idx) => {
      // Add text before match
      if (match.start > lastIndex) {
        parts.push(
          <span key={`text-${idx}`}>
            {lineText.substring(lastIndex, match.start)}
          </span>
        );
      }

      // Add highlighted match
      parts.push(
        <span key={`match-${idx}`} className="search-match-highlight">
          {lineText.substring(match.start, match.end)}
        </span>
      );

      lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < lineText.length) {
      parts.push(
        <span key="text-end">
          {lineText.substring(lastIndex)}
        </span>
      );
    }

    return <>{parts}</>;
  };

  const totalMatches = searchResults.reduce((sum, file) => sum + file.totalMatches, 0);

  return (
    <div className="monaco-search-panel">
      <div className="monaco-search-header">
        <h3>{'🔍 Search'}</h3>
        <button
          className="monaco-search-close"
          onClick={onClose}
          title="Close search panel (Esc)"
          type="button"
        >
          {'✕'}
        </button>
      </div>

      <div className="monaco-search-input-container">
        <div className="monaco-search-input-wrapper">
          <input
            className="monaco-search-input"
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search"
            type="text"
            value={searchText}
          />
          <div className="monaco-search-options">
            <button
              className={`monaco-search-option-btn ${caseSensitive ? 'active' : ''}`}
              onClick={() => setCaseSensitive(!caseSensitive)}
              title="Match Case"
              type="button"
            >
              {'Aa'}
            </button>
            <button
              className={`monaco-search-option-btn ${wholeWord ? 'active' : ''}`}
              onClick={() => setWholeWord(!wholeWord)}
              title="Match Whole Word"
              type="button"
            >
              {'ab'}
            </button>
            <button
              className={`monaco-search-option-btn ${useRegex ? 'active' : ''}`}
              onClick={() => setUseRegex(!useRegex)}
              title="Use Regular Expression"
              type="button"
            >
              {'.*'}
            </button>
          </div>
        </div>

        <div className="monaco-search-filters">
          <input
            className="monaco-search-filter-input"
            onChange={(e) => setIncludePattern(e.target.value)}
            placeholder="files to include (e.g., *.js, src/**)"
            type="text"
            value={includePattern}
          />
          <input
            className="monaco-search-filter-input"
            onChange={(e) => setExcludePattern(e.target.value)}
            placeholder="files to exclude (e.g., node_modules, *.min.js)"
            type="text"
            value={excludePattern}
          />
        </div>
      </div>

      <div className="monaco-search-results-header">
        {isSearching ? (
          <span className="monaco-search-status">{'⏳ Searching...'}</span>
        ) : searchText ? (
          <span className="monaco-search-status">
            {totalMatches > 0 ?
              `${totalMatches} result${totalMatches !== 1 ? 's' : ''} in ${searchResults.length} file${searchResults.length !== 1 ? 's' : ''}` :
              'No results found'}
          </span>
        ) : (
          <span className="monaco-search-status">{'Enter text to search'}</span>
        )}
      </div>

      <div className="monaco-search-results">
        {searchResults.map((fileResult) => {
          const isExpanded = expandedFiles[fileResult.filePath] !== false; // Default to expanded

          return (
            <div key={fileResult.filePath} className="monaco-search-file-result">
              <div
                className="monaco-search-file-header"
                onClick={() => toggleFileExpanded(fileResult.filePath)}
                role="button"
                tabIndex={0}
              >
                <span className="monaco-search-expand-icon">
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span className="monaco-search-file-icon">{'📄'}</span>
                <span className="monaco-search-file-name">{fileResult.fileName}</span>
                <span className="monaco-search-file-path">{fileResult.filePath}</span>
                <span className="monaco-search-match-count">
                  {fileResult.totalMatches}
                </span>
              </div>

              {isExpanded && (
                <div className="monaco-search-matches">
                  {fileResult.matches.map((match, idx) => (
                    <div
                      key={`${fileResult.filePath}-${match.lineNumber}-${idx}`}
                      className="monaco-search-match-line"
                      onClick={() => handleMatchClick(fileResult.filePath, match.lineNumber)}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="monaco-search-line-number">
                        {match.lineNumber}
                      </span>
                      <span className="monaco-search-line-text">
                        {highlightMatches(match.lineText, match.matches)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

MonacoSearchPanel.propTypes = {
  onClose: PropTypes.func.isRequired,
  onFileSelect: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired
};

export default MonacoSearchPanel;
